import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import RibbonNavbar from "../../components/RibbonNavbar";
import SearchableDataGrid from "../../components/SearchableDataGrid";
import { iepaProd } from "../../constants/global";

const ManageRoles = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  // ================= FETCH ROLES =================
  const fetchRoles = async () => {
    try {
      const res = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/roles/active/org/1`
      );

      const data = await res.json();

      if (Array.isArray(data)) {
        const formatted = data.map((role, index) => ({
          ...role,
          id: role.id,
          sno: index + 1,
        }));

        setRoles(formatted);
      } else {
        setRoles([]);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setRoles([]);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // ================= FILTER =================
  const filteredRows = roles.filter((role) => {
    const search = filterText.toLowerCase();
    return (
      role.name?.toLowerCase().includes(search) ||
      role.type?.toLowerCase().includes(search) ||
      role.description?.toLowerCase().includes(search) ||
      role.status?.toLowerCase().includes(search)
    );
  });

  // ================= COLUMNS =================
  const columns = [
    { field: "sno", headerName: "S No", width: 80 },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: 1,
    },
    {
      field: "endDate",
      headerName: "End Date",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
    },
    {
      field: "personaEnabledStatus",
      headerName: "Persona Enabled Status",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
    },
  ];

  const handleDeactivate = async () => {
    if (selectedRows.length === 0) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to deactivate selected role(s)?"
    );

    if (!confirmDelete) return;

    try {
      await Promise.all(
        selectedRows.map((roleId) =>
          fetch(
            `${iepaProd.iepaAdapterSrvAPI}/adapters/roles/${roleId}/deactivate/org/1`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
        )
      );

      alert("Role(s) deactivated successfully");

      await fetchRoles();
      setSelectedRows([]);
    } catch (error) {
      console.error("Deactivate failed:", error);
      alert("Failed to deactivate role(s)");
    }
  };

  return (
    <Box m="20px">
      {/* HEADER + RIBBON */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Manage Roles" />

        <RibbonNavbar
          selectedRows={selectedRows}
          showAdd
          showEdit
          showView
          showDelete
          onAdd={() => navigate("../Roles")}
          onEdit={() => {
            const selectedRole = roles.find(
              (role) => role.id === selectedRows[0]
            );

            navigate("/Roles", {
              state: {
                roleData: selectedRole,
                isEditMode: true,
              },
            });
          }}
          onView={() => {
            const selectedRole = roles.find(
              (role) => role.id === selectedRows[0]
            );

            navigate("/Roles", {
              state: {
                roleData: selectedRole,
                isEditMode: false,
              },
            });
          }}
          onDelete={handleDeactivate}
          disabledEdit={selectedRows.length === 0}
          disabledView={selectedRows.length === 0}
          disabledDelete={selectedRows.length === 0}
        />
      </Box>

      {/* GRID */}
      <Box
        height="78vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid grey",
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            fontSize: "14px",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: colors.blueAccent[900],
          },
        }}
      >
        <SearchableDataGrid
          rows={filteredRows}
          columns={columns}
          checkboxSelection
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedRows(newSelection)
          }
          components={{ Toolbar: GridToolbar }}
        />
      </Box>
    </Box>
  );
};

export default ManageRoles;